"use client";

import React, { useState, useEffect } from "react";
import { Clock, Cpu, Users } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface PlaceholderItem {
  id: string;
  track: {
    en: string;
    vi: string;
  };
  badge: {
    en: string;
    vi: string;
  };
  title: {
    en: string;
    vi: string;
  };
  description: {
    en: string;
    vi: string;
  };
  status: {
    en: string;
    vi: string;
  };
}
const defaultMascot = {
  id: "mascot-default",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot walking with tablet"
};
const sectionContent = {
  eyebrow: {
    en: "VERIFIED LAB MILESTONES",
    vi: "CỘT MỐC LAB THỰC NGHIỆM ĐƯỢC CHỨNG THỰC"
  },
  title: {
    en: "From first curiosity to autonomous builds",
    vi: "Từ tò mò ban đầu đến mô hình tự vận hành"
  },
  description: {
    en: "Every competition trophy begins with a student learning how to diagnose their first error code. Hear how our young engineers built confidence through hands-on lab experiments.",
    vi: "Mỗi chiếc cúp giải đấu đều khởi nguồn từ việc học viên biết tự chẩn đoán dòng mã lỗi đầu tiên. Lắng nghe cách các kỹ sư nhí bồi đắp bản lĩnh và tư duy Engineering qua từng buổi thực nghiệm chuyên sâu tại lab."
  },
  mentorRatioBadge: {
    en: "1:6 MENTOR RATIO",
    vi: "TỶ LỆ 1 HLV : 6 HỌC VIÊN"
  },
  stats: [{
    value: "1:6",
    label: {
      en: "Mentor to Student",
      vi: "Tỷ Lệ Giảng Viên / Học Viên"
    },
    icon: Users
  }],
  ctaButtons: {
    records: {
      en: "See Competition Records",
      vi: "Xem Thành Tích Thi Đấu"
    },
    trial: {
      en: "Book a Free Trial",
      vi: "Đăng Ký Học Thử Miễn Phí"
    }
  }
};
const placeholderCards: PlaceholderItem[] = [{
  id: "1",
  track: {
    en: "Early Engineering (Ages 4–6)",
    vi: "Kỹ Thuật Nhí (4–6 Tuổi)"
  },
  badge: {
    en: "In Progress",
    vi: "Đang Cập Nhật"
  },
  title: {
    en: "Junior Explorer Stories Coming Soon",
    vi: "Hồ Sơ Học Viên Nhí Sắp Ra Mắt"
  },
  description: {
    en: "Real build milestones and tactile engineering problem-solving moments from our youngest lab cohort will be documented and featured here.",
    vi: "Các cột mốc chế tạo thực tế và hành trình khám phá kỹ thuật đầu đời của học viên ROBOTO sẽ sớm được ghi nhận và đăng tải tại đây."
  },
  status: {
    en: "Lab Profile Coming Soon",
    vi: "Hồ Sơ Sắp Xuất Bản"
  }
}, {
  id: "2",
  track: {
    en: "LEGO & Mechanics (Ages 7–9)",
    vi: "LEGO & Cơ Khí (7–9 Tuổi)"
  },
  badge: {
    en: "In Progress",
    vi: "Đang Cập Nhật"
  },
  title: {
    en: "Mechanical Build Stories Coming Soon",
    vi: "Nhật Ký Chế Tạo Máy Sắp Ra Mắt"
  },
  description: {
    en: "Genuine project showcases highlighting gear transmission, structural calculations, and mechanical prototyping will be published here soon.",
    vi: "Dự án chế tạo cơ cấu máy, tỷ số truyền động và tư duy giải quyết vấn đề kỹ thuật của học viên sẽ sớm được xuất bản tại đây."
  },
  status: {
    en: "Lab Profile Coming Soon",
    vi: "Hồ Sơ Sắp Xuất Bản"
  }
}, {
  id: "3",
  track: {
    en: "Coding & Python (Ages 10–13)",
    vi: "Lập Trình Python (10–13 Tuổi)"
  },
  badge: {
    en: "In Progress",
    vi: "Đang Cập Nhật"
  },
  title: {
    en: "Algorithmic Code Stories Coming Soon",
    vi: "Hồ Sơ Dự Án Lập Trình Sắp Ra Mắt"
  },
  description: {
    en: "First-hand software debugging journals, algorithmic breakthroughs, and autonomous program builds will be showcased here shortly.",
    vi: "Hồ sơ dự án thuật toán, nhật ký giải quyết lỗi code và các mô hình phần mềm tự vận hành thực tế sẽ sớm được giới thiệu tại đây."
  },
  status: {
    en: "Lab Profile Coming Soon",
    vi: "Hồ Sơ Sắp Xuất Bản"
  }
}, {
  id: "4",
  track: {
    en: "Competitive Robotics (Ages 14–16)",
    vi: "Robotics Thi Đấu (14–16 Tuổi)"
  },
  badge: {
    en: "In Progress",
    vi: "Đang Cập Nhật"
  },
  title: {
    en: "Tournament Team Stories Coming Soon",
    vi: "Nhật Ký Đội Tuyển Sắp Ra Mắt"
  },
  description: {
    en: "Authentic competition build logs, sensor integration case studies, and tournament milestones will be featured upon documentation completion.",
    vi: "Nhật ký thiết kế phần cứng, tích hợp cảm biến và các chặng đua thi đấu thực nghiệm sẽ được công bố ngay khi hoàn tất tổng hợp tư liệu."
  },
  status: {
    en: "Lab Profile Coming Soon",
    vi: "Hồ Sơ Sắp Xuất Bản"
  }
}];
export default function Stories() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem("roboto_lang");
      if (savedLang === "en" || savedLang === "vi") {
        setLang(savedLang);
      }
    } catch {
      // Ignore storage access restrictions
    }
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "vi" || event.newValue === "en")) {
        setLang(event.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleLanguageChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLanguageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="student-success-stories" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      {/* Decorative Lab Blueprint Rail & Subtle Pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.025] bg-[radial-gradient(hsl(var(--primary))_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16">
        {/* Static Floating Decorative Mascot */}
        <div className="hidden md:block absolute top-0 right-8 md:right-12 lg:right-16 pointer-events-none select-none z-0">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} className="h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" loading="lazy" />
        </div>

        {/* Section Header */}
        <div className="relative z-10 mb-12 md:mb-16 max-w-2xl lg:max-w-3xl md:pr-32 lg:pr-0">
          {/* Circuit Spec Notch & Eyebrow */}
          <div className="flex items-center gap-2 mb-3">
            <div className="h-1.5 w-1.5 rounded-sm bg-secondary" />
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary">
              {sectionContent.eyebrow[lang]}
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
            {sectionContent.title[lang]}
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {sectionContent.description[lang]}
          </p>
        </div>

        {/* 4-Column Coming Soon Placeholder Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {placeholderCards.map((item, i) => <motion.div key={item.id} variants={cardVariants} data-index={i}>
              <Card className="group h-full flex flex-col justify-between border border-border bg-card rounded-xl p-0 overflow-hidden shadow-xs hover:border-secondary transition-[border-color,box-shadow] duration-250 hover:shadow-md">
                <div>
                  {/* Lab Placeholder Header Area */}
                  <div className="relative h-44 w-full bg-muted/50 border-b border-border/60 flex flex-col items-center justify-center p-6 text-center">
                    <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[radial-gradient(hsl(var(--primary))_1px,transparent_1px)] [background-size:16px_16px]" />
                    <div className="flex size-12 items-center justify-center rounded-lg bg-card border border-border text-secondary shadow-xs mb-3">
                      <Clock className="size-5 text-secondary" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/10 border border-secondary/20 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-secondary">
                      {item.badge[lang]}
                    </span>
                  </div>

                  {/* Card Main Content */}
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-1 mb-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                        <Cpu className="size-3.5 text-secondary shrink-0" />
                        <span>{item.track[lang]}</span>
                      </div>
                      <h3 className="font-serif text-lg font-bold text-foreground tracking-tight mt-1">
                        {item.title[lang]}
                      </h3>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description[lang]}
                    </p>
                  </CardContent>
                </div>

                {/* Footer Info */}
                <div className="px-6 pb-6 pt-3 border-t border-border/40 mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {sectionContent.mentorRatioBadge[lang]}
                  </span>
                  <span className="font-mono text-[11px] text-secondary font-medium">
                    {item.status[lang]}
                  </span>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Supporting Lab Statistics Strip */}
        <div className="mt-14 rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-8 sm:gap-12">
            {sectionContent.stats.map((stat, i) => {
            const IconComponent = stat.icon;
            return <div key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex size-11 items-center justify-center rounded-lg bg-secondary/10 border border-secondary/20 text-secondary">
                    <IconComponent className="size-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-xs text-muted-foreground font-sans">
                      {stat.label[lang]}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto justify-end">
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/ProjectsCompetitionsPage">
                {sectionContent.ctaButtons.records[lang]}
              </Link>
            </Button>
            <Button asChild className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/FreeTrialContactPage">
                {sectionContent.ctaButtons.trial[lang]}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}